import React, { useRef, useEffect, useState } from "react";
import EmailEditor, { EditorRef } from "react-email-editor";
import { message} from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getEngagementsBaseUrl } from "src/config/endpoints";

const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();

type EmailEditorModalProps = {
  visible: boolean;

  onClose: () => void;
  campaignData: {
    campaignName: string;
    sendTo: string[];
    excludeList: string[];
    originalCampaignId?: string;
    scheduledDateTime?: dayjs.Dayjs | null;
    emailHtml?: string;
    companyNameEngagements: string; 
  };
};

// Define interface for API response

interface SendCampaignResponse {
  message: string;

  success?: boolean;
}

const EmailEditorModal: React.FC<EmailEditorModalProps> = ({
  visible,

  onClose,

  campaignData,
}) => {
  const emailEditorRef = useRef<EditorRef>(null);
const editorInstanceRef = useRef<any>(null); // use `any` to bypass TS errors
  const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState<boolean>(false);
  const navigate = useNavigate();

const handleEditorReady = () => {
  setEditorLoaded(true);
  if (emailEditorRef.current) {
    editorInstanceRef.current = emailEditorRef.current.editor;
  }

  if (campaignData.emailHtml?.trim()) {
    setShowHtmlPreview(true);
  } else {
    editorInstanceRef.current?.loadBlank({});
  }
};

const handleReviewAndSend = () => {
  if (!editorLoaded || !emailEditorRef.current?.editor) {
    message.warning("Editor is not ready yet!");
    return;
  }

  // Use `.editor.exportHtml` instead of `.exportDesign`
  emailEditorRef.current.editor.exportHtml((data: any) => {
    const { design, html } = data;

    navigate("/account/review-and-send", {
      state: {
         campaignName: campaignData.campaignName,
        replyTo: "support@datatram.com",
       recipients: campaignData.sendTo.join(","),
        subjectLine: "Hot Deals Inside!",
        fromName: "Marketing Team",
        fromEmail: "marketing@datatram.com",
        startDate: dayjs().format("YYYY-MM-DD"),
        endDate: dayjs().add(7, "day").format("YYYY-MM-DD"),
        emailDesign: design, // JSON representation
        emailHtml: html,     // ✅ ready-to-render HTML
      },
    });
  });
};


  const loadHtmlForEditing = (): void => {
    if (campaignData.emailHtml && emailEditorRef.current?.editor) {
      try {

        const design = {
          counters: {
            u_column: 1,

            u_row: 1,

            u_content_html: 1,
          },

          body: {
            id: undefined,

            rows: [
              {
                id: "row_1",

                cells: [1],

                columns: [
                  {
                    id: "column_1",

                    contents: [
                      {
                        type: "html",

                        values: {
                          html: campaignData.emailHtml,
                        },
                      },
                    ],

                    values: {},
                  },
                ],

                values: {},
              },
            ],

            headers: [],

            footers: [],

            values: {},
          },
        };

        emailEditorRef.current.editor.loadDesign(design);

        setShowHtmlPreview(false);
      } catch (error) {

        if (emailEditorRef.current?.editor) {
          emailEditorRef.current.editor.loadBlank({});
        }

        setShowHtmlPreview(false);
      }
    }
  };

  const startFresh = (): void => {
    if (emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.loadBlank({});
    }

    setShowHtmlPreview(false);
  };

  useEffect(() => {
    if (visible) {

      setEditorLoaded(false);

      setShowHtmlPreview(false);
    }
  }, [visible, campaignData]);

  const handleSend = (): void => {
    if (editorLoaded && emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.exportHtml(
        async (data: { html: string; design: Record<string, unknown> }) => {
          const { html } = data;

          // Determine send time - use scheduled time if available, otherwise immediate

          let sendAt: string;

          if (campaignData.scheduledDateTime) {
            sendAt = campaignData.scheduledDateTime.toISOString();
          } else {
            sendAt = new Date().toISOString();
          }

          // Determine which API endpoint to use based on originalCampaignId

          const apiEndpoint = campaignData.originalCampaignId
            ? `${ENGAGEMENTS_BASE_URL}/resend-campaign-email`
            : `${ENGAGEMENTS_BASE_URL}/send-campaign-email`;

          // Prepare payload based on whether it's a resend or new campaign
          const tenantId = localStorage.getItem("tenant_id");
          const payload = campaignData.originalCampaignId
            ? {
                tenant_id: tenantId,
                previous_campaign_id: campaignData.originalCampaignId,
                campaign_name: campaignData.campaignName,
                send_to: campaignData.sendTo,
                dont_send_to: campaignData.excludeList,
                html,
                send_at: sendAt,
                company_name_engagements: campaignData.companyNameEngagements, // ✅ Added here
              }
            : {
                tenant_id: tenantId,
                campaign_name: campaignData.campaignName,
                send_to: campaignData.sendTo,
                dont_send_to: campaignData.excludeList,
                html,
                send_at: sendAt,
                company_name_engagements: campaignData.companyNameEngagements, // ✅ And here
              };

          try {
            const response = await fetch(apiEndpoint, {
              method: "POST",

              headers: {
                "Content-Type": "application/json",

                Authorization: `Bearer ${localStorage.getItem("api_key")}`,
              },

              body: JSON.stringify(payload),
            });

            const result: SendCampaignResponse = await response.json();

            if (response.ok) {
              const scheduleText = campaignData.scheduledDateTime
                ? ` scheduled for ${campaignData.scheduledDateTime.format(
                    "DD/MM/YYYY hh:mm A"
                  )}`
                : "";

              const actionText = campaignData.originalCampaignId
                ? "Campaign resent"
                : "Campaign email sent";

              message.success(`${actionText}${scheduleText} successfully!`);

              navigate("/account/engagement/campaign-analytics");
            } else {
              message.error(`Error: ${result.message}`);
            }
          } catch (error) {
            console.error("❌ API error:", error);

            message.error("Failed to send campaign email");
          }
        }
      );
    } else {
      message.warning("Editor is not ready yet!");
    }
  };


  if (!visible) {
    return null;
  }

  return (
    <div className="email-editor-overlay">
      <div className="email-editor-modal">
        <div className="email-editor-header">
          <span className="headline" style={{ marginLeft: "-1px" }}>
            Email Editor
            {campaignData.originalCampaignId && (
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  marginLeft: "10px",
                }}
              >
                (Recreating Campaign)
              </span>
            )}
          </span>

          <button className="email-editor-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="email-editor-body">
          {showHtmlPreview && (
            <div
              style={{
                position: "absolute",

                top: "0",

                left: "0",

                right: "0",

                bottom: "60px",

                backgroundColor: "rgba(0,0,0,0.8)",

                zIndex: 1000,

                display: "flex",

                flexDirection: "column",

                alignItems: "center",

                justifyContent: "center",

                color: "white",
              }}
            >
              <div
                style={{
                  backgroundColor: "white",

                  padding: "20px",

                  borderRadius: "8px",

                  maxWidth: "500px",

                  textAlign: "center",

                  color: "black",
                }}
              >
                <h3>Previous Campaign Content Available</h3>

                <p>
                  We found the HTML content from your previous campaign. You
                  can:
                </p>

                <div style={{ marginTop: "20px" }}>
                  <button
                    onClick={loadHtmlForEditing}
                    style={{
                      backgroundColor: "#007bff",

                      color: "white",

                      border: "none",

                      padding: "10px 20px",

                      borderRadius: "4px",

                      marginRight: "10px",

                      cursor: "pointer",
                    }}
                  >
                    Load & Edit Previous Content
                  </button>

                  <button
                    onClick={startFresh}
                    style={{
                      backgroundColor: "#6c757d",

                      color: "white",

                      border: "none",

                      padding: "10px 20px",

                      borderRadius: "4px",

                      cursor: "pointer",
                    }}
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          )}

          <EmailEditor
            ref={emailEditorRef}
            onLoad={handleEditorReady}
            options={{
              displayMode: "email",
            }}
          />
        </div>

        <div
          className="center-button-container"
          style={{
            position: "absolute",

            bottom: "10px",

            left: "50%",

            transform: "translateX(-50%)",

            zIndex: 1001, // Ensure it's above other elements
          }}
        >
          <button
            className="button button--primary"
            onClick={handleReviewAndSend}
            disabled={!editorLoaded}
            style={{
              cursor: editorLoaded ? "pointer" : "not-allowed",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              opacity: editorLoaded ? "1" : "0.7",
              display: "flex", // Use flexbox for centering
              alignItems: "center", // Vertical centering
              justifyContent: "center", // Horizontal centering
              height: "40px", // Optional: fix height for vertical alignment
              minWidth: "150px", // Optional: make width consistent
              position: "relative",
              zIndex: 1002,
              marginTop: "20px",
            }}
          >
           {editorLoaded ? "Review & Send" : "Loading Editor..."}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailEditorModal;
